import { posthog } from '@/lib/posthog';
import { DisplaySurveyType, type DisplaySurveyPopoverOptions } from 'posthog-js';
import { useCallback, useEffect, useState } from 'react';

export interface Survey {
  available: boolean;
  open: (options?: Partial<Omit<DisplaySurveyPopoverOptions, 'displayType'>>) => void;
}

interface UseSurveyOptions {
  throttle?: number; // delay between displays in ms
  onSent?: () => void; // what to do when survey is submitted
}

const lastShownKey = (name: string) => `beacon:survey-shown:${name}`;

// open a posthog survey. using names for easy reference
export function useSurvey(name: string, { throttle, onSent }: UseSurveyOptions = {}): Survey {
  const [surveyId, setSurveyId] = useState<string | null>(null);

  useEffect(() => {
    if (!posthog.__loaded) return;

    return posthog.onSurveysLoaded((surveys) => {
      setSurveyId(surveys.find((survey) => survey.name === name)?.id ?? null);
    });
  }, [name]);

  useEffect(() => {
    if (!surveyId || !onSent) return;

    const handler = (event: Event) => {
      if ((event as CustomEvent<{ surveyId: string }>).detail?.surveyId === surveyId) onSent();
    };

    window.addEventListener('PHSurveySent', handler);
    return () => window.removeEventListener('PHSurveySent', handler);
  }, [surveyId, onSent]);

  const open = useCallback<Survey['open']>(
    (options) => {
      if (!surveyId) return;

      if (throttle) {
        const lastShown = Number(localStorage.getItem(lastShownKey(name)) ?? 0);

        if (Date.now() - lastShown < throttle) return;
        localStorage.setItem(lastShownKey(name), String(Date.now()));
      }

      posthog.displaySurvey(surveyId, {
        ignoreConditions: true,
        ignoreDelay: true,
        ...options,
        displayType: DisplaySurveyType.Popover,
      });
    },
    [surveyId, name, throttle],
  );

  return { available: surveyId !== null, open };
}
